import '../style/global.css';

import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import React from 'react';


import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // outline rounded doesn't exist?
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import { ISentenceScore } from '../api/ScoreApi';
import female_avatar_1 from '../asset/female_avatar_1.png';
import female_avatar_2 from '../asset/female_avatar_2.png';
import AddSelfStudyBlueIcon from '../asset/icon-add-self-study-blue.png';
import MicrophoneBlueIcon from '../asset/icon-microphone-blue.png';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import male_avatar from '../asset/male_avatar.png';
import { useKaiTiFontStyles } from '../font/KaiTiFont';

import { useHistory, useLocation } from 'react-router-dom';
import { ScoreGrade, scoreColor, SentenceScoreStatus, sentenceScoreStatus, sentenceScoreStatusMessage } from '../Score';
import { AudioApi } from '../api/AudioApi';
import { ChatApi, IChatLine, IHskDetail } from '../api/ChatApi';
import { ChineseTeacher } from '../api/UserApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useSentenceScoreDialog } from '../context/SentenceScoreDialogContext';
import { scrollElementIntoViewWithFallback } from '../util/scrollUtils';
import { emptyAudioBlob } from './dialogPrompt/DialogPromptComponent';
import { TeacherAudioActions, TeacherAudioReportContext, getSelectedTeacherSpeakerRole } from './TeacherAudioReportButton';


/*

conversation - intermeidlate level 
20 sentences
strucutre - vocabulary - 20.

each sentence, osmetimes 1 word, sometimes 2 or 3 words.

1 conversaiton per class

20 classes???

*/

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end', // adjust this to 'flex-start' if the message is from the other person
        margin: theme.spacing(2),
    },
    bubble: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#3ca6a6', // adjust this to 'grey' if the message is from the other person
        padding: theme.spacing(1),
        margin: theme.spacing(1),
        maxWidth: '100%',
        [theme.breakpoints.up('sm')]: {
            maxWidth: '90%', // from 600px and up
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: '80%', // from 600px and up
        },
        [theme.breakpoints.up('lg')]: {
            maxWidth: '70%', // from 900px and up
        },
    },
    text: {
        color: 'black',
        margin: theme.spacing(1),
    },
    avatar: {
        margin: theme.spacing(1),
    },
}));

export interface ChatBubbleProps {
    isTeacher: boolean;
    message: string | null | undefined;
    messageAudio: Blob | null | undefined;
    showAvatar?: boolean;
    sentenceScore?: ISentenceScore | null;
    setChatBubbleDone?: (done: boolean) => void;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement> | undefined;
    useChineseFont?: boolean;
    listenSentenceId?: number;
    chatLine?: IChatLine | null;
    showChatButton?: boolean;
    showChatPinyin?: boolean;
    isAnimated?: boolean;
    dialogPromptId?: number | null;
    audioRole?: string;
    teacherAudioReportContext?: TeacherAudioReportContext | null;
}

const getAudioDuration = (blob: Blob): Promise<number> => {
    if (blob == emptyAudioBlob) {
        // Return a resolved promise with duration based on message length
        return Promise.resolve(0);
    }
    return new Promise((resolve, reject) => {
        const audio = new Audio(URL.createObjectURL(blob));

        // Set up event listeners
        audio.addEventListener('loadedmetadata', () => {
            resolve(audio.duration);
        });

        audio.addEventListener('error', () => {
            reject(new Error("Failed to load audio metadata."));
        });
    });
};

const normalizeChatBubbleMessage = (message: string | null | undefined): string => {
    if (message == null) return '';
    return typeof message === 'string' ? message : String(message);
};



const anyEnglishRegex = /[a-zA-Z]/;
const markdownOrIconRegex = /(\*\*[\s\S]+?\*\*|__[\s\S]+?__|<Microphone\/>|<Speaker\/>|<Info\/>|<Feedback\/>)/gi;
const markdownTokenRegex = /^(\*\*[\s\S]+?\*\*|__[\s\S]+?__)$/;
const iconTokenRegex = /^(<Microphone\/>|<Speaker\/>|<Info\/>|<Feedback\/>)$/i;
const iconTagList = ['<Microphone/>', '<Speaker/>', '<Info/>', '<Feedback/>'];

function getIconTagAt(text: string, index: number): string | null {
    const remaining = text.substring(index).toLowerCase();
    return iconTagList.find(tag => remaining.startsWith(tag.toLowerCase())) ?? null;
}
const englishRegexTest = /[\x00-\x7FÀ-ſĀ-ɏḀ-ỿāáǎàēéěèīíǐìōóǒòūúǔùǖǘǚǜ]+/;
const chineseRegexTest = /[一-鿿]/;

function splitPlainTextByScript(text: string): string[] {
    const segments: string[] = [];
    let current = '';
    let currentKind: 'english' | 'chinese' | null = null;
    let pendingNeutral = '';

    const getKind = (char: string): 'english' | 'chinese' | 'neutral' => {
        if (chineseRegexTest.test(char)) return 'chinese';
        if (englishRegexTest.test(char)) return 'english';
        return 'neutral';
    };

    for (const char of text) {
        const kind = getKind(char);
        if (kind === 'neutral') {
            if (currentKind == null) {
                pendingNeutral += char;
            } else {
                current += char;
            }
            continue;
        }

        if (currentKind == null) {
            currentKind = kind;
            current = pendingNeutral + char;
            pendingNeutral = '';
            continue;
        }

        if (currentKind === kind) {
            current += char;
        } else {
            segments.push(current);
            currentKind = kind;
            current = char;
        }
    }

    if (current) {
        segments.push(current + pendingNeutral);
    } else if (pendingNeutral) {
        segments.push(pendingNeutral);
    }

    return segments;
}

const ChatBubble = ({ isTeacher, message: messageProp, messageAudio, showAvatar = true, sentenceScore = null, setChatBubbleDone,
    chatBubbleContainerRef, useChineseFont = false, listenSentenceId = -1, chatLine = null, showChatButton = false, showChatPinyin = false,
    isAnimated = true, dialogPromptId = null, audioRole = 'chat_bubble', teacherAudioReportContext = null }: ChatBubbleProps) => {

    let message = normalizeChatBubbleMessage(messageProp);

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();
    const disableAutoScrollForSettingsLesson = typeof document !== 'undefined'
        && Boolean(document.querySelector('[data-agent-settings-view="true"]'));

    const { playAudio } = useAudioPlayer();

    const [messageAudioDuration, setMessageAudioDuration] = React.useState<number | null>(null);
    const [englishCharAudioDuration, setEnglishCharAudioDuration] = React.useState<number | null>(null);
    const [chineseCharAudioDuration, setChineseCharAudioDuration] = React.useState<number | null>(null);
    const hasAudio = messageAudio != null && messageAudio !== emptyAudioBlob;
    const [hasAudioDonePlaying, setHasAudioDonePlaying] = React.useState(true);

    const { openDialog: openInfoDetailDialog } = useSentenceScoreDialog();

    const setChatBubbleDoneRef = React.useRef(setChatBubbleDone);
    const chatBubbleDoneNotifiedRef = React.useRef(false);
    const completedMessageRef = React.useRef<string | null>(null);
    const currentMessageRef = React.useRef(message);
    const sentenceScoreAudioPlayedRef = React.useRef(false);
    const messageAudioPlayedRef = React.useRef<Blob | null>(null);

    React.useEffect(() => {
        setChatBubbleDoneRef.current = setChatBubbleDone;
    }, [setChatBubbleDone]);

    React.useEffect(() => {
        currentMessageRef.current = message;
    }, [message]);

    React.useEffect(() => {
        if (chatBubbleDoneNotifiedRef.current && completedMessageRef.current === message) {
            return;
        }
        chatBubbleDoneNotifiedRef.current = false;
        completedMessageRef.current = null;
        sentenceScoreAudioPlayedRef.current = false;
        messageAudioPlayedRef.current = null;
        setHasAudioDonePlaying(!hasAudio);
    }, [message, messageAudio, sentenceScore, hasAudio]);

    const notifyChatBubbleDone = React.useCallback(() => {
        const notify = setChatBubbleDoneRef.current;
        if (!notify || chatBubbleDoneNotifiedRef.current) return;
        chatBubbleDoneNotifiedRef.current = true;
        completedMessageRef.current = currentMessageRef.current;
        notify(true);
    }, []);

    React.useEffect(() => {
        if (message == undefined || message == null || String(message).trim().length <= 0) {
            notifyChatBubbleDone();
        };
        if (sentenceScore != null) {
            notifyChatBubbleDone();
        }
        if (sentenceScore && sentenceScore.audioData && !sentenceScoreAudioPlayedRef.current) {
            sentenceScoreAudioPlayedRef.current = true;
            playAudio(sentenceScore.audioData)
        }
    }, [message, notifyChatBubbleDone, playAudio, sentenceScore]);

    React.useEffect(() => {
        const chineseEnglishDurationRatio = 6
        let chineseCharCount = 0;
        for (const ch of message) {
            if (isChineseChar(ch)) chineseCharCount++
        }
        if (messageAudio === undefined) {
            setMessageAudioDuration(null);
            setEnglishCharAudioDuration(null);
            setChineseCharAudioDuration(null);
            return;
        }
        if (messageAudio != null) {
            getAudioDuration(messageAudio)
                .then(duration => {
                    setMessageAudioDuration(duration)
                    const englishCharAudioDuration = duration / ((message.length - chineseCharCount) + chineseCharCount * chineseEnglishDurationRatio)
                    const englishCharAudioDurationMs = Math.min(englishCharAudioDuration * 1000, 100);
                    const chineseCharAudioDurationMs = Math.min(englishCharAudioDuration * chineseEnglishDurationRatio * 1000, 600);
                    // console.log("getAudioDuration()", duration, chineseCharCount, englishCharAudioDuration)
                    setEnglishCharAudioDuration(englishCharAudioDurationMs)
                    setChineseCharAudioDuration(chineseCharAudioDurationMs)
                    // console.log("getAudioDuration()", englishCharAudioDuration * 1000, englishCharAudioDuration * chineseEnglishDurationRatio * 1000)
                })
                .catch(err => {
                    setMessageAudioDuration(0);
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
                .finally(() => {
                    if (chatBubbleDoneNotifiedRef.current) return;
                    if (messageAudioPlayedRef.current === messageAudio) return;
                    messageAudioPlayedRef.current = messageAudio;
                    playAudio(messageAudio, {
                        onEnded: () => setHasAudioDonePlaying(true),
                        onError: () => setHasAudioDonePlaying(true),
                    });
                });
        } else {
            setEnglishCharAudioDuration(25)
            setChineseCharAudioDuration(25 * chineseEnglishDurationRatio)
            setMessageAudioDuration((message.length - chineseCharCount) * 0.025 + chineseCharCount * 0.025 * chineseEnglishDurationRatio);
        }
    }, [history, location, message, messageAudio, playAudio, setErrorMessage]);

    const classes = useStyles();

    const studentName = localStorage.getItem('studentName');
    const studentAvatar = localStorage.getItem('studentAvatar');
    const teacher = localStorage.getItem('teacher');
    let teacherAvatar;

    if (message != undefined && message != null && String(message).trim().length > 0) {
        if (teacher == ChineseTeacher.Male) {
            teacherAvatar = male_avatar
            message = message.replace(/<TeacherName\/>/gi, "Li Laoshi");
        } else if (teacher == ChineseTeacher.Female) {
            teacherAvatar = female_avatar_1
            message = message.replace(/<TeacherName\/>/gi, "Liao Laoshi");
        } else if (teacher == ChineseTeacher.Female2) {
            teacherAvatar = female_avatar_2
            message = message.replace(/<TeacherName\/>/gi, "Fan Laoshi");
        } else if (teacher == null) {
            teacherAvatar = null
            message = message.replace(/<TeacherName\/>/gi, "your teacher");
        } else {
            throw Error("unknown selected teacher name in local storage " + teacher);
        }

        if (studentName) {
            message = message.replace(/<StudentName\/>/gi, studentName);
        }

        message = message.replace(/<Answer\/>/gi, "_________");
    }

    const justifyContent = isTeacher ? 'flex-start' : 'flex-end';
    const backgroundColor = isTeacher ? '#F2F7FF' : '#FFFCF5';
    const messageAudioReportContext: TeacherAudioReportContext | null = hasAudio
        ? {
            dialogPromptId,
            audioText: message,
            audioRole,
            textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
            ...teacherAudioReportContext,
        }
        : null;
    const listenSentenceAudioReportContext: TeacherAudioReportContext | null = listenSentenceId > 0
        ? {
            sentenceId: listenSentenceId,
            dialogPromptId,
            audioText: message,
            audioRole: `${audioRole}_sentence`,
            textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        }
        : null;

    const handleSpeakerClick = (sentenceId: number) => {
        const audioPromise = AudioApi.getTeacherSentence(sentenceId)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const [hskDetailList, setHskDetailList] = React.useState<IHskDetail[] | null>(null);
    const [isChatLineDetailDialogOpen, setIsChatLineDetailDialogOpen] = React.useState(false);

    const handleChatLineDetailClick = (chatLineId: number) => {
        // Show dialog immediately with "Loading..."
        setHskDetailList(null);
        setErrorMessage("");

        // console.log("Fetching HSK details for:", chatLineId);
        ChatApi.getHskDetailDetail(chatLineId) // Assuming `id` is the correct property to identify the chat line.
            .then(response => {
                // Assuming the response data structure matches what you want to display
                // console.log("API Success:", response.data);
                setHskDetailList(response.data);
                setIsChatLineDetailDialogOpen(true);
            })
            .catch(err => {
                console.error("API Error:", err);
                setHskDetailList([]);
                setErrorMessage("Failed to load sentence details.");
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }

    const handlePronounceClick = (chatLineId: number) => {
        ChatApi.getChatLine(chatLineId) // Assuming `id` is the correct property to identify the chat line.
            .then(response => {
                const chatLine = response.data
                history.push(`/sentence?text=` + chatLine.line);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    };


    const [displayedMessage, setDisplayedMessage] = React.useState('');
    const [displayedMessageSegments, setDisplayedMessageSegments] = React.useState<string[]>([]);

    const [currentIndex, setCurrentIndex] = React.useState(0);

    React.useEffect(() => {
        setDisplayedMessage('');
        setDisplayedMessageSegments([]);
        setCurrentIndex(0);
    }, [message]);

    const chatBubbleFont = useKaiTiFontStyles("black", '1.5rem');
    const chatFont = useChineseFont ? chatBubbleFont.customFont : classes.text;

    const kaiTiFontClass = useKaiTiFontStyles();
    const storedLearnWithCharacter = localStorage.getItem('learnWithCharacter');
    const learnWithCharacter = storedLearnWithCharacter ? JSON.parse(storedLearnWithCharacter) : false;

    const isChineseChar = (char: string): boolean => /[\u4E00-\u9FFF]/.test(char);

    function randomTailWeighted(min: number, max: number): number {
        const range = max - min;
        // Get a value between -1 and 1
        const r = Math.random() * 2 - 1;

        // Use squared value to weight toward tails
        const tailBias = r >= 0 ? 1 : -1;
        const biased = tailBias * Math.pow(r, 2); // now between -1 and 1, but U-shaped

        return biased * (range / 2);
    }

    React.useEffect(() => {
        // console.log("useEffect()", message, messageAudioDuration, chineseCharAudioDuration, englishCharAudioDuration)
        if (message == undefined && hasAudio == false) {
            // console.log("useEffect() early exit")
            notifyChatBubbleDone();
            return;
        }

        if (message && messageAudioDuration && chineseCharAudioDuration && englishCharAudioDuration) {
            // console.log("useEffect() if statement")
            // delay is the delay for this currentIndex to appear
            let delay = 25;

            if (currentIndex > 0) {
                const currentChar = message[currentIndex];
                if (isChineseChar(currentChar)) {
                    delay = randomTailWeighted(-chineseCharAudioDuration, chineseCharAudioDuration) + chineseCharAudioDuration
                } else {
                    delay = randomTailWeighted(-englishCharAudioDuration, englishCharAudioDuration) + englishCharAudioDuration
                }
            }

            // If no animation, use fixed small delay
            if (isAnimated === false) {
                delay = 25;
            }

            const timeout = setTimeout(() => {
                if (!message) return "";
                if (isAnimated) {
                    setDisplayedMessage((prevMessage) => {
                        if (currentIndex >= message.length) {
                            return prevMessage;
                        }

                        const iconTag = getIconTagAt(message, currentIndex);
                        if (iconTag) {
                            return prevMessage + iconTag;
                        }

                        return prevMessage + message[currentIndex];
                    });

                    // update currentIndex separately
                    const iconTag = getIconTagAt(message, currentIndex);
                    if (iconTag) {
                        setCurrentIndex(prev => prev + iconTag.length);
                    } else {
                        setCurrentIndex(prev => prev + 1);
                    }

                } else {
                    setDisplayedMessage(message)
                    setCurrentIndex(message.length)
                    // calculateNumRows()
                }

            }, delay);

            return () => { clearTimeout(timeout); }
        }
    }, [currentIndex, hasAudio, isAnimated, message, messageAudioDuration, englishCharAudioDuration, chineseCharAudioDuration, notifyChatBubbleDone]);

    React.useEffect(() => {
        let messageTextDone = true;
        if (message) messageTextDone = currentIndex >= message.length

        let audioDone = true;
        if (hasAudio) audioDone = hasAudioDonePlaying

        if (messageTextDone && audioDone) {
            // console.log("chat bubbone done 2 for message " + message)
            notifyChatBubbleDone();
        }
    }, [currentIndex, message, hasAudio, hasAudioDonePlaying, notifyChatBubbleDone])

    React.useEffect(() => {
        // Keep markdown/icon tokens intact, but split plain text into
        // Chinese/English runs so mixed-language lines get correct fonts.
        const rawSegments = displayedMessage
            .split(markdownOrIconRegex)
            .filter((segment) => segment !== "");
        const segments = rawSegments.flatMap((segment) => {
            if (markdownTokenRegex.test(segment) || iconTokenRegex.test(segment)) {
                return [segment];
            }
            return splitPlainTextByScript(segment);
        });

        setDisplayedMessageSegments(segments);

    }, [displayedMessage])

    function renderMarkdown(text: string): React.ReactNode[] {
        const parts = text.split(/(\*\*.+?\*\*|__.+?__)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <span key={i} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</span>;
            }
            if (part.startsWith('__') && part.endsWith('__')) {
                const inner = part.slice(2, -2);
                // Don't treat pure-underscore sequences (e.g. from <Answer/>) as italic.
                if (inner.length > 0 && !inner.startsWith('_')) {
                    return <span key={i} style={{ fontStyle: 'italic' }}>{inner}</span>;
                }
            }
            return <React.Fragment key={i}>{part}</React.Fragment>;
        });
    }




    // refs outside the effect to persist across renders
    const autoScrollLock_lastScrollAtRef = React.useRef(0);
    const autoScrollLock_trailingTimerRef = React.useRef<number | null>(null);

    const autoScrollLock_reqAnimationFrameIdRef = React.useRef<number | null>(null);
    const autoScrollLock_touchStartYRef = React.useRef<number | null>(null);
    const autoScrollLock_LockedRef = React.useRef<boolean>(false);
    const [autoScrollLock_Locked, setAutoScrollLock_Locked] = React.useState(false);

    // Optional global unlock for future integration without prop changes
    React.useEffect(() => {
        (window as any).__autoScrollLock_globalUnlock = () => setAutoScrollLock_Locked(false);
        return () => {
            try { delete (window as any).__autoScrollLock_globalUnlock; } catch { (window as any).__autoScrollLock_globalUnlock = undefined; }
        };
    }, []);

    React.useEffect(() => {
        if (disableAutoScrollForSettingsLesson) return;
        if (chatBubbleContainerRef && chatBubbleContainerRef.current) {
            const el = chatBubbleContainerRef.current;
            if (!el) return;

            const now = Date.now();
            const elapsed = now - autoScrollLock_lastScrollAtRef.current;

            const doScroll = () => {
                if (autoScrollLock_LockedRef.current) return;
                autoScrollLock_lastScrollAtRef.current = Date.now();
                if (!autoScrollLock_LockedRef.current) {
                    scrollElementIntoViewWithFallback(el, null, "smooth"); //scroll to bottom
                }

            };

            if (elapsed >= 250) {
                // okay to run immediately
                autoScrollLock_reqAnimationFrameIdRef.current = requestAnimationFrame(doScroll);
            } else {
                // schedule one trailing run if not already scheduled
                if (autoScrollLock_trailingTimerRef.current == null) {
                    autoScrollLock_trailingTimerRef.current = window.setTimeout(() => {
                        autoScrollLock_trailingTimerRef.current = null;
                        autoScrollLock_reqAnimationFrameIdRef.current = requestAnimationFrame(doScroll);
                    }, 250 - elapsed);
                }
            }

            // cleanup any pending trailing call if deps change/unmount
            return () => {
                if (autoScrollLock_trailingTimerRef.current != null) {
                    clearTimeout(autoScrollLock_trailingTimerRef.current);
                    autoScrollLock_trailingTimerRef.current = null;
                }
            };
        }
    }, [chatBubbleContainerRef, displayedMessage, message, autoScrollLock_Locked, disableAutoScrollForSettingsLesson]);

    // Keep ref in sync to avoid stale closure checks
    React.useEffect(() => {
        if (disableAutoScrollForSettingsLesson) return;
        autoScrollLock_LockedRef.current = autoScrollLock_Locked;
        if (autoScrollLock_Locked) {
            // cancel any scheduled animation frame or timeout to prevent pending auto-scroll
            if (autoScrollLock_reqAnimationFrameIdRef.current != null) {
                cancelAnimationFrame(autoScrollLock_reqAnimationFrameIdRef.current);
                autoScrollLock_reqAnimationFrameIdRef.current = null;
            }
            if (autoScrollLock_trailingTimerRef.current != null) {
                clearTimeout(autoScrollLock_trailingTimerRef.current);
                autoScrollLock_trailingTimerRef.current = null;
            }
        }
    }, [autoScrollLock_Locked, disableAutoScrollForSettingsLesson]);

    // Lock/unlock solely via wheel direction (desktop); no scrollTop or touch detection
    React.useEffect(() => {
        if (disableAutoScrollForSettingsLesson) return;
        // Wheel: direct intent. Negative deltaY = user scrolling up; positive = down
        const onWheel = (e: WheelEvent) => {
            if (e.deltaY < -1) {
                setAutoScrollLock_Locked(true);
                if (autoScrollLock_reqAnimationFrameIdRef.current != null) { cancelAnimationFrame(autoScrollLock_reqAnimationFrameIdRef.current); autoScrollLock_reqAnimationFrameIdRef.current = null; }
                if (autoScrollLock_trailingTimerRef.current != null) { clearTimeout(autoScrollLock_trailingTimerRef.current); autoScrollLock_trailingTimerRef.current = null; }
            }
            // else if (e.deltaY > 10) {
            //     setAutoScrollLocked(false);
            // }
        };
        window.addEventListener('wheel', onWheel, { passive: true } as AddEventListenerOptions);
        return () => {
            window.removeEventListener('wheel', onWheel as EventListener);
        };
    }, [disableAutoScrollForSettingsLesson]);

    // Touch devices: detect vertical movement to lock/unlock
    React.useEffect(() => {
        if (disableAutoScrollForSettingsLesson) return;
        const onTouchStart = (e: TouchEvent) => {
            if (!e.touches || e.touches.length === 0) return;
            autoScrollLock_touchStartYRef.current = e.touches[0].clientY;
        };
        const onTouchMove = (e: TouchEvent) => {
            if (!e.touches || e.touches.length === 0) return;
            const y = e.touches[0].clientY;
            if (autoScrollLock_touchStartYRef.current == null) { autoScrollLock_touchStartYRef.current = y; return; }
            const dy = y - autoScrollLock_touchStartYRef.current; // +dy: finger moved down (content scrolls up)
            autoScrollLock_touchStartYRef.current = y;
            if (dy > 8) {
                setAutoScrollLock_Locked(true);
                if (autoScrollLock_reqAnimationFrameIdRef.current != null) { cancelAnimationFrame(autoScrollLock_reqAnimationFrameIdRef.current); autoScrollLock_reqAnimationFrameIdRef.current = null; }
                if (autoScrollLock_trailingTimerRef.current != null) { clearTimeout(autoScrollLock_trailingTimerRef.current); autoScrollLock_trailingTimerRef.current = null; }
            }
            // else if (dy < -8) {
            //     setAutoScrollLocked(false);
            // }
        };
        window.addEventListener('touchstart', onTouchStart, { passive: true } as AddEventListenerOptions);
        window.addEventListener('touchmove', onTouchMove, { passive: true } as AddEventListenerOptions);
        return () => {
            window.removeEventListener('touchstart', onTouchStart as EventListener);
            window.removeEventListener('touchmove', onTouchMove as EventListener);
        };
    }, [disableAutoScrollForSettingsLesson]);

    function chatBubbleMessage() {

        if (sentenceScore) {
            const scoreStatus = sentenceScoreStatus(sentenceScore);
            const scoreStatusMessage = sentenceScoreStatusMessage(sentenceScore);
            const hidePinyinScore = scoreStatus == SentenceScoreStatus.NoSpeech;

            return <>
                <Stack direction="row">
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                        {/* The audio element */}
                        {sentenceScore.audioData && (
                            <IconButton onClick={() => playAudio(sentenceScore.audioData!)} sx={{ px: 1 }}>
                                <img src={SpeakerBlueIcon} alt="Play" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                            </IconButton>
                        )}
                        {scoreStatusMessage && (
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    color: scoreStatus == SentenceScoreStatus.TargetMismatch ? '#A33A00' : '#5A4A00',
                                    mr: 1,
                                }}
                            >
                                {scoreStatusMessage}
                            </Typography>
                        )}
                        {!hidePinyinScore && (
                            sentenceScore.pinyin_list_spoken.map((pinyinElement, index) =>
                                <Stack direction="column" key={index} >
                                    <Stack alignItems="center" justifyContent="center"
                                        sx={{
                                            backgroundColor: `${scoreColor(sentenceScore!.score_grade[index])}22`,   // 22 is 13.33% opacity
                                            borderTopLeftRadius: 4, borderTopRightRadius: 4,
                                            px: 1
                                        }}>
                                        <Typography sx={{
                                            marginLeft: '0.5em', marginRight: '0.5em',
                                        }}>
                                            {pinyinElement}
                                        </Typography>
                                        {learnWithCharacter && (
                                            <Typography className={kaiTiFontClass.customFont} sx={{
                                                fontSize: '24px'
                                            }}>
                                                {sentenceScore!.text[index]}</Typography>
                                        )}
                                    </Stack>
                                    <Stack alignItems="center" justifyContent="center"
                                        sx={{
                                            backgroundColor: `${scoreColor(ScoreGrade.Good)}22`,
                                            borderBottomLeftRadius: 4, borderBottomRightRadius: 4,
                                            px: 1
                                        }}>
                                        <Typography sx={{
                                            marginLeft: '0.5em', marginRight: '0.5em',
                                        }}>
                                            {sentenceScore!.pinyin_list_transcript[index]}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            )
                        )}
                    </Stack>
                    <Stack direction="column" alignItems="center" justifyContent="center">
                        {/* Add the detail icon button */}
                        <Tooltip title="More Details" arrow>
                            <IconButton
                                aria-label="More details"
                                onClick={() => openInfoDetailDialog(sentenceScore, true)}
                                sx={{ alignSelf: 'center', p: 1 }}
                            >
                                <InfoOutlinedIcon sx={{ width: 30, height: 30, color: '#0062FF' }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack >
            </>
        }
        if (chatLine && chatLine.pinyin.some(p => p.trim() !== '')) {
            // Show only the portion that has been animated so far
            const visibleChars = chatLine.line.slice(0, currentIndex);

            return (
                <Stack direction="row" alignItems="flex-end" flexWrap="wrap" rowGap={1}>
                    {visibleChars.split('').map((lineElement, index) => {
                        // Determine if the lineElement contains Chinese characters
                        // this is a proxy if this is Conversation or Office Hours chat.
                        // const lineElement = chatLine!.line[rowIndex * elementsPerRow + index];
                        const containsEnglish = anyEnglishRegex.test(lineElement) || lineElement === "";
                        // Apply appropriate font based on whether it's Chinese or English
                        const font = containsEnglish ? chatFont : kaiTiFontClass.customFont;
                        // Apply a font size of 24px for Chinese segments
                        const fontSize = containsEnglish ? 'inherit' : '24px';

                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: 'inline-flex',           // Keep each pair together
                                    flexDirection: 'column',          // Pinyin on top, char below
                                    alignItems: 'center',
                                    marginX: '0.1em',                 // Small spacing between units
                                    whiteSpace: 'nowrap',             // Prevent Pinyin wrapping separately
                                }}
                            >
                                {showChatPinyin && (
                                    <Typography
                                        sx={{
                                            px: '0.1em',
                                            display: 'inline-block',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {/* {pinyinElement} */}
                                        {chatLine!.pinyin[index]}
                                    </Typography>
                                )}
                                <Typography
                                    className={font}
                                    sx={{
                                        fontSize,
                                    }}
                                >
                                    {/* {chatLine!.line[rowIndex * elementsPerRow + index]} */}
                                    {lineElement}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            );
        }

        return <div className="message-container"> {/* Wrap the segments in a container */}
            {
                displayedMessageSegments.map((segment, index) => {
                    const normalizedSegment = segment.toLowerCase();
                    // Determine if the segment contains Chinese characters
                    const fontCheckText = segment.replace(/\*\*|__/g, "");
                    const isEnglish = englishRegexTest.test(fontCheckText) || fontCheckText == "";

                    // Apply appropriate font based on whether it's Chinese or English
                    const font = isEnglish ? chatFont : kaiTiFontClass.customFont; // Replace with actual font names
                    // Apply a font size of 24px for Chinese segments
                    const fontSize = isEnglish ? 'inherit' : '24px';

                    if (normalizedSegment === '<microphone/>') {
                        return <img key={index} src={MicrophoneBlueIcon} alt="Microphone" style={{ width: '18px', height: '18px' }} />
                    } else if (normalizedSegment === '<speaker/>') {
                        return <img key={index} src={SpeakerBlueIcon} alt="Speaker" style={{ width: '18px', height: '18px' }} />
                    } else if (normalizedSegment === '<info/>') {
                        return <InfoOutlinedIcon key={index} sx={{ width: 16, height: 16, color: '#0062FF' }} />
                    } else if (normalizedSegment === '<feedback/>') {
                        return <FeedbackOutlinedIcon key={index} sx={{ width: 16, height: 16, color: '#0062FF', verticalAlign: 'middle' }} />
                        // return <img key={index} src={InfoBlueIcon} style={{ width: '18px', height: '18px' }} />
                    }
                    return (
                        <Typography key={index} className={font} style={{ fontSize, verticalAlign: 'middle' }} display="inline" >
                            {renderMarkdown(segment)}
                        </Typography>
                    );
                })
            }
        </div>
    }


    if (message == undefined || message == null || String(message).trim().length <= 0) {
        return <></>
    }

    return (<>
        {messageAudioDuration != null && (
            <div className={classes.root} data-testid="chat-bubble" style={{ justifyContent }}>
                {isTeacher ? (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems="stretch" // ensures child can grow in cross-axis
                        spacing={1}
                        sx={{ width: '100%' }}
                    >
                        <Stack direction={{ xs: 'row', sm: 'column' }} alignItems="center">
                            {showAvatar ? (
                                <img
                                    src={teacherAvatar || ''}
                                    alt="Teacher avatar"
                                    style={{ maxHeight: '4em', maxWidth: '4em' }}
                                />
                            ) : null}
                            {(showChatButton && chatLine) && (
                                <Stack direction='row' alignItems="center">
                                    <IconButton
                                        aria-label="Open sentence detail"
                                        onClick={() => handleChatLineDetailClick(chatLine.id)}
                                    >
                                        <InfoOutlinedIcon sx={{ width: 24, height: 24, color: '#0062FF' }} />
                                    </IconButton>
                                    <Tooltip title="Add to Self-Study" arrow>
                                        <IconButton aria-label="Add to Self-Study" onClick={() => handlePronounceClick(chatLine.id)}>
                                            <img src={AddSelfStudyBlueIcon} style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}

                        </Stack>
                        <Stack direction='row'
                            sx={{
                                flexGrow: 1,
                                minWidth: 0,       // ✅ critical to prevent shrinking with siblings
                                display: 'flex',    // optional: helps layout inside bubble
                                alignItems: 'center',
                            }}>
                            {(listenSentenceId > 0) && (
                                <TeacherAudioActions
                                    context={listenSentenceAudioReportContext}
                                    onPlay={() => handleSpeakerClick(listenSentenceId)}
                                    speakerSize={24}
                                    speakerAriaLabel="Play sentence audio"
                                />
                            )}
                            {hasAudio && messageAudio && listenSentenceId <= 0 && (
                                <TeacherAudioActions
                                    context={messageAudioReportContext}
                                    onPlay={() => playAudio(messageAudio)}
                                    speakerSize={24}
                                    speakerAriaLabel="Replay message audio"
                                />
                            )}
                            <Box
                                className={`${classes.bubble} border-light`}
                                style={{ backgroundColor }}>
                                {chatBubbleMessage()}
                            </Box>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack
                        direction={{ xs: 'column-reverse', sm: 'row' }}
                        alignItems={{ xs: 'flex-end', sm: 'stretch' }} // ⬅ this line!
                        spacing={1}
                        sx={{ width: '100%' }}
                        justifyContent='flex-end' // right align in row mod
                    >
                        <Stack direction='row'
                            sx={{
                                flexGrow: 1,
                                minWidth: 0,       // ✅ critical to prevent shrinking with siblings
                                display: 'flex',    // optional: helps layout inside bubble
                                alignItems: 'center',
                                justifyContent: 'flex-end', // align content to the right
                            }}>
                            <Box
                                className={`${classes.bubble} border-light`}
                                style={{ backgroundColor }}
                                sx={{
                                    marginLeft: 'auto',   // ⬅ push bubble to the right
                                    display: 'inline-flex',
                                }}
                            >
                                {chatBubbleMessage()}
                            </Box>
                            {hasAudio && messageAudio && listenSentenceId <= 0 && (
                                <TeacherAudioActions
                                    context={messageAudioReportContext}
                                    onPlay={() => playAudio(messageAudio)}
                                    speakerSize={24}
                                    speakerAriaLabel="Replay message audio"
                                />
                            )}
                            {(listenSentenceId > 0) && (
                                <TeacherAudioActions
                                    context={listenSentenceAudioReportContext}
                                    onPlay={() => handleSpeakerClick(listenSentenceId)}
                                    speakerSize={24}
                                    speakerAriaLabel="Play sentence audio"
                                />
                            )}
                        </Stack>
                        <Stack direction={{ xs: 'row', sm: 'column' }} alignItems="center">
                            {(showChatButton && chatLine) && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <IconButton
                                            aria-label="Open sentence detail"
                                            onClick={() => handleChatLineDetailClick(chatLine.id)}
                                    >
                                        <InfoOutlinedIcon sx={{ width: 24, height: 24, color: '#0062FF' }} />
                                    </IconButton>
                                    <Tooltip title="Add to Self-Study" arrow>
                                        <IconButton aria-label="Add to Self-Study" onClick={() => handlePronounceClick(chatLine.id)}>
                                            <img src={AddSelfStudyBlueIcon} style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            )}
                            {showAvatar ? (
                                <img
                                    src={studentAvatar || ''}
                                    alt="Student avatar"
                                    style={{ maxHeight: '4em', maxWidth: '4em' }}
                                />
                            ) : null}
                        </Stack>
                    </Stack>
                )}
            </div>
        )}
        {/* Chat Line Detail Dialog */}
        <Dialog open={isChatLineDetailDialogOpen} onClose={() => setIsChatLineDetailDialogOpen(false)}>
            <DialogTitle>Sentence Detail</DialogTitle>
            <DialogContent>
                <TableContainer component={Paper} sx={{
                    border: '2px solid lightgrey',
                    borderRadius: '8px',
                    overflow: 'hidden', // Ensures the borderRadius applies to the table as well
                    borderColor: 'lightgrey', // Explicitly setting the border color
                    // Additional styles to enforce the border color if necessary
                    '& .MuiPaper-root': {
                        borderColor: 'lightgrey',
                    }
                }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Word</TableCell>
                                <TableCell align="right">HSK Level</TableCell>
                                <TableCell align="right">Pinyin</TableCell>
                                <TableCell align="right">Definition</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {hskDetailList ? hskDetailList.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell component="th" scope="row">{row.word}</TableCell>
                                    <TableCell align="right">{row.hsk_level}</TableCell>
                                    <TableCell align="right">{row.pinyin}</TableCell>
                                    <TableCell align="right">{row.definition}</TableCell>
                                </TableRow>
                            )) : <TableRow><TableCell colSpan={4} align="center">Loading...</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsChatLineDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    </>);
};

export default ChatBubble;
